/* eslint-disable max-classes-per-file */

import $ from '../jQuery/jQuery';

class Module {
    constructor(moduleName, moduleClass) {
        this.name = moduleName;
        this.class = moduleClass;
        this.instances = new Map();
    }
}

class ModuleRegistry {
    constructor() {
        this._registeredModules = new Map();
        this._autoApplySelectors = new Map();
        this._autoApplyModules = new Map();
        this._addEventListeners();
    }

    _addEventListeners() {
        document.addEventListener('DOMContentLoaded', () => {
            this._initAutoApplyModules();
        });
    }

    registerModule(moduleClassName, moduleClass) {
        if (!moduleClassName) {
            throw new TypeError('moduleClassName must be a non-empty string');
        }

        const isValidModuleClass = ModuleRegistry._validateModuleClass(moduleClass);

        if (!isValidModuleClass) {
            return null;
        }

        const hasModule = this._registeredModules.has(moduleClassName);

        if (hasModule) {
            const existingModule = this._registeredModules.get(moduleClassName);

            if (moduleClass !== existingModule.class) {
                throw new TypeError(`A module with name '${moduleClassName}' is already registered`);
            }

            return existingModule;
        }

        const module = new Module(moduleClassName, moduleClass);

        this._registeredModules.set(moduleClassName, module);

        return module;
    }

    autoApplyModule(moduleClassName, selector) {
        const hasModule = this._registeredModules.has(moduleClassName);

        if (!hasModule) {
            throw new TypeError(`No module with name '${moduleClassName}' was registered`);
        }

        const trimmedSelector = selector ? selector.trim() : selector;

        if (!trimmedSelector) {
            throw new TypeError('selector must be a non-empty string');
        }

        const module = this._registeredModules.get(moduleClassName);

        this._processAutoApplyModule(module, trimmedSelector);
    }

    applyModule(moduleClassName, element) {
        const hasModule = this._registeredModules.has(moduleClassName);

        if (!hasModule) {
            throw new TypeError(`No module with name '${moduleClassName}' was registered`);
        }

        if (!(element instanceof HTMLElement)) {
            throw new TypeError('element must be a HTMLElement');
        }

        const module = this._registeredModules.get(moduleClassName);

        ModuleRegistry._initModule(module, element, document);
    }

    /**
     * Insert a HTML string into a container
     * and initialize all modules
     * within this HTML string.
     * @param {HTMLElement | jQuery} container The container element
     * @param {string} html HTML string
     * @param {object} options Options object
     * Following options are supported:
     * - {boolean} append:
     *   If true the content will be appended,
     *   otherwise the existing content will be replaced
     * - {boolean} prepend:
     *   If true the content will be prepended,
     *   otherwise the existing content will be replaced
     *   (prepend overrides append)
     * - {object} selectorCallbacks:
     *   An object whose keys are CSS selectors and values are functions.
     *   The function will be executed for each element matching the selector
     *   with the parameters element and document fragment.
     */
    insertHTML(container, html, options = null) {
        if (container && typeof container.length === 'number') {
            // unwrap jQuery object
            // eslint-disable-next-line no-param-reassign, prefer-destructuring
            container = container[0];
        }

        if (!container) {
            return;
        }

        if (!options || (!options.prepend && !options.append)) {
            container.innerHTML = '';
        }

        const trimmedHtml = `${html || ''}`.trim();

        if (!trimmedHtml) {
            return;
        }

        const documentFragment = ModuleRegistry._parseHtml(trimmedHtml);

        this._autoApplyModules.forEach((module, selector) => {
            const elements = documentFragment.querySelectorAll(selector);

            for (let i = 0, iLen = elements.length; i < iLen; i++) {
                const element = elements[i];
                ModuleRegistry._initModule(module, element, documentFragment, true);
            }
        });

        if (options && options.selectorCallbacks) {
            Object.keys(options.selectorCallbacks).forEach((selector) => {
                const callback = options.selectorCallbacks[selector];

                if (typeof callback !== 'function') {
                    return;
                }

                const elements = documentFragment.querySelectorAll(selector);

                for (let i = 0, iLen = elements.length; i < iLen; i++) {
                    const element = elements[i];
                    callback(element, documentFragment);
                }
            });
        }

        const referenceNode = options && options.prepend ? container.firstChild : null;

        container.insertBefore(documentFragment, referenceNode);
    }

    _processAutoApplyModule(module, selector) {
        this._autoApplyModules.set(selector, module);

        if (ModuleRegistry._isDomReady()) {
            const $elements = $(selector);

            $elements.each((i, el) => {
                // init the module immediately if `DOMContentLoaded already fired
                ModuleRegistry._initModule(module, el, document, true);
            });
        } else {
            const hasSelector = this._autoApplySelectors.has(selector);
            let modulesForSelector;

            if (hasSelector) {
                modulesForSelector = this._autoApplySelectors.get(selector);
            } else {
                modulesForSelector = new Set();
                this._autoApplySelectors.set(selector, modulesForSelector);
            }

            modulesForSelector.add(module);
        }
    }

    _initAutoApplyModules() {
        this._autoApplySelectors.forEach((modules, selector) => {
            const $elements = $(selector);

            $elements.each((i, el) => {
                modules.forEach((module) => {
                    ModuleRegistry._initModule(module, el, document, true);
                });
            });
        });
    }

    static _initModule(module, element, document, silent = false) {
        if (module.instances.has(element)) {
            if (silent) {
                return;
            }

            throw new TypeError(`Module '${module.name}' already instantiated on given element`);
        }

        const $element = $(element);
        const elementData = $element.data();

        // eslint-disable-next-line new-cap
        const moduleInstance = new module.class($element, elementData, document);
        element.instance = moduleInstance;



        module.instances.set(element, moduleInstance);

        moduleInstance.init();

    }

    static _validateModuleClass(moduleClass) {
        if (!moduleClass || typeof moduleClass !== 'function') {
            throw new TypeError('moduleClass must be a function');
        }

        if (typeof moduleClass.prototype.init !== 'function') {
            throw new TypeError('A module must have a "init" method');
        }

        return true;
    }

    static _isDomReady() {
        const { readyState } = document;
        return readyState === 'interactive' || readyState === 'complete';
    }

    static _parseHtml(html) {
        return document.createRange().createContextualFragment(html);
    }
}

export default new ModuleRegistry();
